import { useEffect, ReactNode } from "react";
import { registerContextData, extractPaths, unregisterContextData } from "./token/contextRegistry";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { usePuckActionPlan } from "src/components/Puck/config/contexts/PuckActionPlanContext";
import { usePuckCompanyScore } from "src/components/Puck/config/contexts/PuckCompanyScoreContext";
import { useStrategy } from "src/context/Strategy/useStrategy";
import { EntityScoresProvider } from "src/context/Scores/provider";
import { useOrganizationDiagnosticFacts } from "src/api/client-api/diagnosticFacts/useOrganizationDiagnosticFacts";
import { buildCalcSuggestionPaths, buildScoreTitleById } from "./token/buildCalcSuggestionPaths";

type TokenSuggestionsProviderProps = {
    children: ReactNode;
};

export function TokenSuggestionsProvider(props: Readonly<TokenSuggestionsProviderProps>) {
    const { children } = props;
    const foodpilotContext = useFoodpilotContext();

    const scoreContext = usePuckCompanyScore();
    const actionPlanContext = usePuckActionPlan();
    const strategyContext = useStrategy();

    const { facts: calcFacts, catalog: calcCatalog } = useOrganizationDiagnosticFacts("calc.", { catalog: true });

    useEffect(() => {
        const withAdditionalPaths = (paths: { path: string; isArray: boolean }[], additionalPaths: string[]) => {
            const existing = new Set(paths.map((path) => path.path));
            return [
                ...paths,
                ...additionalPaths
                    .filter((path) => !existing.has(path))
                    .map((path) => ({
                        path,
                        isArray: false,
                    })),
            ].sort((a, b) => a.path.localeCompare(b.path));
        };

        try {
            if (foodpilotContext) {
                if (foodpilotContext.user?.trustedUser) {
                    const userPaths = extractPaths(foodpilotContext.user.trustedUser);
                    registerContextData("user", userPaths);
                }

                if (foodpilotContext.selectedCompany) {
                    const companyPaths = extractPaths(foodpilotContext.selectedCompany);
                    registerContextData("company", companyPaths);
                }

                if (foodpilotContext.currentCampaign) {
                    const campaignPaths = withAdditionalPaths(extractPaths(foodpilotContext.currentCampaign), [
                        "current_year",
                    ]);
                    registerContextData("campaign", campaignPaths);
                }
            }

            if (scoreContext) {
                const scoreContextPaths = extractPaths(scoreContext);
                registerContextData("scores", scoreContextPaths);
            }

            if (strategyContext) {
                const strategyPaths = extractPaths(strategyContext);
                registerContextData("strategyData", strategyPaths);
            }

            registerContextData(
                "pdf",
                withAdditionalPaths([], ["generated_at", "current_year", "short_date", "medium_date", "full_date"]),
            );
            registerContextData(
                "actionPlan",
                withAdditionalPaths(actionPlanContext ? extractPaths(actionPlanContext) : [], [
                    "initial_diagnostic_year",
                    "reduction_percentage",
                ]),
            );
            unregisterContextData("metric");
            unregisterContextData("metrics");
            unregisterContextData("actionsPlan");
            unregisterContextData("action_plan");

            const scoreTitleById = buildScoreTitleById(foodpilotContext?.scores);
            const calcPaths = buildCalcSuggestionPaths(calcFacts ?? {}, scoreTitleById, calcCatalog);

            if (calcPaths.length > 0) {
                registerContextData("calc", calcPaths);
            } else {
                unregisterContextData("calc");
            }
        } catch (error) {
            console.error("Error registering token paths:", error);
        }
    }, [foodpilotContext, scoreContext, actionPlanContext, strategyContext, calcFacts, calcCatalog]);

    const companyScores = foodpilotContext.getScoresByType("company") || [];
    const hasCompanyScores = companyScores.length > 0;

    if (!hasCompanyScores) {
        return children;
    }

    return <EntityScoresProvider entity="company">{children}</EntityScoresProvider>;
}
